import { InboxOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Popconfirm, Progress, Upload } from 'antd';
import { useAppDispatch } from 'app/hooks';
import axios, { CancelTokenSource } from 'axios';
import { RevxLoader } from 'components/RevxLoader';
import { success } from 'components/RevxMessage';
import { RevxSpin } from 'components/RevxSpin';
import { Headline, UnderlineText } from 'components/RevxTypography';
import { PartialSKU } from 'domains/skuDomain';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uploadFileService from 'services/uploadService';
import {
  GALLERY_IMAGE_ACCEPT,
  UPLOAD_FILE_ADDRESS_PREFIX,
} from 'utils/constants';
import './GalleryImage.less';

type Props = { sku: PartialSKU; showError: boolean };

export const GalleryImage = ({ sku, showError }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const curItem = sku;
  const galleryImage = curItem.images?.[0];
  const cancelSources = useRef<CancelTokenSource[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const onCancelUpload = () => {
    cancelSources.current.forEach((source) => {
      source.cancel();
    });
  };

  return (
    <div className="single-sku-gallery-image">
      <Headline>{t('platform.sku.form.label.galleryImage')}</Headline>
      {galleryImage && (
        <div className="single-sku-gallery-image-pic-wrapper">
          <img
            className="single-sku-gallery-image-pic"
            src={UPLOAD_FILE_ADDRESS_PREFIX + galleryImage}
            alt="sku"
          />
        </div>
      )}
      <RevxSpin
        spinning={isUploading}
        indicator={
          isUploading && uploadProgress > 0 ? (
            <div>
              <Progress type="circle" percent={uploadProgress} width={50} />
              {uploadProgress > 0 && uploadProgress < 100 && (
                <Popconfirm
                  placement="topRight"
                  title={t('common.uploadConfirmation')}
                  icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                  onCancel={() => {
                    onCancelUpload();
                  }}
                  okText={t('common.uploadContinueText')}
                  cancelText={t('common.uploadCancelText')}
                >
                  <UnderlineText
                    color="green"
                    style={{ width: 50, textAlign: 'center' }}
                  >
                    {t('common.cancel')}
                  </UnderlineText>
                </Popconfirm>
              )}
            </div>
          ) : (
            <RevxLoader loading />
          )
        }
      >
        <Upload.Dragger
          showUploadList={false}
          style={{ height: 220 }}
          disabled={curItem.readonly}
          listType="picture-card"
          accept={GALLERY_IMAGE_ACCEPT}
          beforeUpload={async (file) => {
            setIsUploading(true);
            const source = axios.CancelToken.source();
            cancelSources.current.push(source);
            const ret = await uploadFileService.upSkuImageFile({
              file,
              cancelTokenObject: {
                token: source.token,
              },
              onUploadProgress: (progress) => {
                const ratio = progress.loaded / file.size;
                if (ratio >= 1) {
                  setTimeout(() => {
                    setUploadProgress(0);
                  }, 120);
                }
                setUploadProgress(Math.ceil(ratio * 100));
              },
            });

            setIsUploading(false);
            setUploadProgress(0);
            if (ret) {
              await dispatch.sku.updateCurItemForSave({
                ...curItem,
                images: [new URL(ret.path).pathname.replace(/^\//, '')],
              });

              success(t('platform.sku.changesSavedTip'));
            }

            return Promise.reject();
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t('platform.sku.form.tip.uploadDrag')}
          </p>
          <p className="ant-upload-text">{t('platform.sku.form.tip.or')}</p>
          <p className="ant-upload-text">
            {t('platform.sku.form.tip.uploadSelect')}
          </p>
        </Upload.Dragger>
      </RevxSpin>
      {showError && (
        <div className="ant-form-item-explain-error" style={{ marginTop: 10 }}>
          {t('platform.sku.galleryImageRequired')}
        </div>
      )}
    </div>
  );
};
